import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CloudDownload as CloudDownloadIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Refresh as ResetIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Edit as EditIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  DataObject as DataObjectIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  GetApp as GetAppIcon,
  DeleteOutline as DeleteOutlineIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatAmount, formatDateOnly, formatDateTime, initializeFormats, getFormatConfig } from '../../../utils/global';
import { useToast } from '../../hooks/useToast';
import Toast from '../../common/Toast';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { useApHub } from '../ApHub/ApHubContext';
import ApHubBreadcrumb from '../ApHub/ApHubBreadcrumb';
import InvoiceUploadDialog from './InvoiceUploadDialog';
import InvoicePreviewDrawer from './InvoicePreviewDrawer';
import InvoiceManualEntryDialog from './InvoiceManualEntryDialog';
import PendingInvoicesTab from './PendingInvoicesTab';
import { fetchDraftUploadCount as loadDraftUploadCount } from './draftUploadsApi';
import InvoiceContentDialog from './InvoiceContentDialog';
import VendorInvoiceBankDetailsDialog from './VendorInvoiceBankDetailsDialog';
import { buildInvoiceExportRows, downloadInvoiceExport } from './invoiceExportUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateBlueButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateTabsSx,
  harmonateDataGridSx,
  harmonateLinkSx,
  harmonateSectionHeaderSx,
  harmonatePlainIconButtonSx,
} from '../../../theme/harmonateStyles';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const getCurrentUserDisplayName = () => {
  const first = (localStorage.getItem('first_name') || '').trim();
  const last = (localStorage.getItem('last_name') || '').trim();
  const fullName = `${first} ${last}`.trim();
  return fullName || localStorage.getItem('username') || localStorage.getItem('email') || null;
};

const invoiceStageAction = (displayStatus) => {
  if (displayStatus === 'Draft' || displayStatus === 'Rejected') return 'delete';
  if (displayStatus === 'Pending Approval' || displayStatus === 'Approved') return 'revert';
  return null;
};
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const deriveDisplayStatus = (row) => {
  const paymentStatus = (row.paymentStatus || '').toUpperCase();
  const workflowStatus = (row.workflowStatus || '').toUpperCase();
  if (paymentStatus === 'PAID') return 'Paid';
  if (workflowStatus === 'APPROVED') return 'Approved';
  if (workflowStatus === 'PENDING_APPROVAL') return 'Pending Approval';
  if (workflowStatus === 'REJECTED') return 'Rejected';
  if (workflowStatus === 'DRAFT') return 'Draft';
  return workflowStatus || paymentStatus || 'Draft';
};

const formatWorkflowStatusLabel = (value) => {
  const normalized = (value || 'DRAFT').toUpperCase();
  const labels = {
    DRAFT: 'Draft',
    PENDING_APPROVAL: 'Pending Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };
  return labels[normalized] || value || 'Draft';
};

const formatPaymentStatusLabel = (value) => {
  const normalized = (value || 'UNPAID').toUpperCase();
  const labels = {
    UNPAID: 'Unpaid',
    PAID: 'Paid',
    PARTIAL: 'Partial',
  };
  return labels[normalized] || value || 'Unpaid';
};

const statusChipProps = (status) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'paid') return { color: 'info', variant: 'filled' };
  if (normalized === 'approved') return { color: 'success', variant: 'filled' };
  if (normalized === 'pending approval') return { color: 'warning', variant: 'filled' };
  if (normalized === 'rejected') return { color: 'error', variant: 'outlined' };
  if (normalized === 'draft') return { color: 'default', variant: 'outlined', sx: { bgcolor: '#f5f5f5' } };
  return { color: 'default', variant: 'outlined' };
};

const TAB_KEYS = ['all', 'draft', 'pending_approval', 'approved', 'paid', 'rejected', 'by_vendors'];

const invoicePaidAmount = (inv) => Number(inv.invoicePaymentsReceived) || 0;

const invoiceTotalAmount = (inv) => Number(inv.invoiceTotalAmountDue) || 0;

const invoiceRemainingAmount = (inv) => {
  if (inv.invoiceRemainingBalanceDue != null) {
    return Number(inv.invoiceRemainingBalanceDue) || 0;
  }
  return Math.max(0, invoiceTotalAmount(inv) - invoicePaidAmount(inv));
};

const TabLabel = ({ text, count, tone = 'default', active = false }) => {
  const tones = {
    default: { bg: active ? ht.white : ht.white, color: active ? ht.green : ht.text },
    success: { bg: active ? ht.white : 'rgba(2, 107, 29, 0.12)', color: active ? ht.green : ht.green },
    info: { bg: active ? ht.white : 'rgba(46, 166, 160, 0.15)', color: active ? ht.green : ht.darkBlue },
    warning: { bg: active ? ht.white : 'rgba(239, 63, 63, 0.12)', color: active ? ht.green : ht.red },
  };
  const { bg, color } = tones[tone] || tones.default;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {text}
      <Box
        component="span"
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: 11,
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: '22px',
          textAlign: 'center',
          bgcolor: bg,
          color,
        }}
      >
        {count}
      </Box>
    </Box>
  );
};

const defaultFilters = {
  businessUnitId: '',
  vendorId: '',
  invoiceDateFrom: '',
  invoiceDateTo: '',
  dueDateFrom: '',
  dueDateTo: '',
  status: '',
};

const parseHeaderAdditionalData = (header) => {
  try {
    const raw = header?.headerAdditionalData;
    if (!raw) return {};
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
};

const getInvoiceFileName = (inv) => {
  const meta = parseHeaderAdditionalData(inv);
  return meta.fileName || meta.sourceFileName || meta.originalFileName || meta.file_name || '';
};

const normalizeVendorName = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Stable group key for By Vendors: real vendorId, else name-based key when OCR left vendor unmatched. */
const getInvoiceVendorGroupKey = (inv) => {
  if (inv?.vendorId) return String(inv.vendorId);
  const nameKey = normalizeVendorName(inv?.vendorName);
  return nameKey ? `name:${nameKey}` : 'unknown';
};

const invoiceMatchesVendorGroup = (inv, target) => {
  if (!target) return false;
  if (getInvoiceVendorGroupKey(inv) === String(target.vendorId || '')) return true;
  const targetName = normalizeVendorName(target.vendorName);
  if (!targetName) return false;
  return normalizeVendorName(inv?.vendorName) === targetName;
};

const invoiceMatchesVendorSearch = (inv, query, vendorCodeById) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;

  const vendorCode = String(vendorCodeById[inv.vendorId] || '').toLowerCase();
  const fileName = String(getInvoiceFileName(inv) || '').toLowerCase();
  const invoiceNumber = String(inv.invoiceNumber || '').toLowerCase();
  const vendorName = String(inv.vendorName || '').toLowerCase();
  const invoiceSummary = String(inv.invoiceSummary || '').toLowerCase();
  const amountText = String(inv.invoiceTotalAmountDue ?? '').toLowerCase();

  // Match human-facing vendor code / id text, not internal UUID (avoids "9" matching random UUID digits).
  const vendorIdText = String(inv.vendorId || '').toLowerCase();
  const matchesVendorId =
    q.length >= 8 && vendorIdText.includes(q);

  return (
    vendorName.includes(q)
    || vendorCode.includes(q)
    || matchesVendorId
    || invoiceNumber.includes(q)
    || fileName.includes(q)
    || invoiceSummary.includes(q)
    || amountText.includes(q)
  );
};

const lastRefreshLabel = (value, dateTimeFormat) => {
  if (!value) return 'Never';
  return formatDateTime(value, dateTimeFormat) || 'Never';
};

const Invoices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();
  const { canAdd, canUpload, canExport, canDelete } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);
  const {
    businessUnits,
    selectedBusinessUnitId,
    selectedBusinessUnitName,
    setSelectedBusinessUnitId,
  } = useApHub();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState(null);

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuInvoice, setRowMenuInvoice] = useState(null);
  const [contentDialogInvoice, setContentDialogInvoice] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [previewDraft, setPreviewDraft] = useState(null);
  const [previewInitialTab, setPreviewInitialTab] = useState(0);

  const [createAnchor, setCreateAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [draftUploadCount, setDraftUploadCount] = useState(0);
  const [draftUploadRefreshKey, setDraftUploadRefreshKey] = useState(0);
  const [vendorBankDetailsTarget, setVendorBankDetailsTarget] = useState(null);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorHistoryTarget, setVendorHistoryTarget] = useState(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [overwriteExistingMappings, setOverwriteExistingMappings] = useState(false);
  const [sageMappingLastRefreshAt, setSageMappingLastRefreshAt] = useState(null);
  const [stageAction, setStageAction] = useState(null);
  const [stageActionSaving, setStageActionSaving] = useState(false);

  const config = getFormatConfig();
  const isDraftUploadsTab = TAB_KEYS[activeTab] === 'draft';
  const isByVendorsTab = TAB_KEYS[activeTab] === 'by_vendors';

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'draft' || tab === 'pending') {
      setActiveTab(TAB_KEYS.indexOf('draft'));
    } else if (tab === 'pending_approval') {
      setActiveTab(TAB_KEYS.indexOf('pending_approval'));
    } else if (tab === 'rejected') {
      setActiveTab(TAB_KEYS.indexOf('rejected'));
    }
  }, [searchParams]);

  useEffect(() => {
    const invoiceId = searchParams.get('invoiceId');
    if (!invoiceId) return;
    setPreviewInvoiceId(invoiceId);
    setPreviewDraft(null);
    setPreviewInitialTab(2);
    setPreviewOpen(true);
  }, [searchParams]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !tenantId) return;

    let cancelled = false;

    (async () => {
      let businessUnitId = searchParams.get('businessUnitId');
      if (!businessUnitId) {
        try {
          const headerData = await apiFetch(`conductor/ap-invoice-headers/${editId}`);
          businessUnitId = headerData?.businessUnitId || '';
        } catch (err) {
          if (!cancelled) showError(err.message || 'Failed to open invoice for editing');
          return;
        }
      }

      if (cancelled) return;

      if (businessUnitId) setSelectedBusinessUnitId(businessUnitId);
      setEditInvoiceId(editId);
      setManualOpen(true);

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('edit');
      nextParams.delete('businessUnitId');
      setSearchParams(nextParams, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, tenantId, setSelectedBusinessUnitId, setSearchParams, showError]);

  const vendorMap = useMemo(
    () => Object.fromEntries(vendors.map((v) => [v.vendorId, v.vendorName])),
    [vendors]
  );
  const vendorCodeById = useMemo(
    () => Object.fromEntries(vendors.map((v) => [v.vendorId, v.vendorCode || ''])),
    [vendors]
  );
  const businessUnitMap = useMemo(
    () => Object.fromEntries(businessUnits.map((bu) => [bu.businessUnitId, bu.businessUnitName])),
    [businessUnits]
  );
  const userScopedInvoices = useMemo(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return allInvoices;
    const allowed = new Set(businessUnits.map((bu) => bu.businessUnitId));
    return allInvoices.filter((inv) => inv.businessUnitId && allowed.has(inv.businessUnitId));
  }, [allInvoices, businessUnits]);

  const fetchDropdowns = useCallback(async () => {
    if (!tenantId) return;
    try {
      const vendorData = await apiFetch(`conductor/vendor-masters/tenant/${tenantId}`);
      setVendors(Array.isArray(vendorData) ? vendorData : []);
    } catch (err) {
      console.error('Failed to load filter dropdowns', err);
    }
  }, [tenantId]);

  const fetchInvoices = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!tenantId) {
      setAllInvoices([]);
      if (!silent) setLoading(false);
      return;
    }
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await apiFetch(`conductor/ap-invoice-headers/tenant/${tenantId}`);
      setAllInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!silent) {
        setError(err.message || 'Failed to load invoices');
        showError(err.message || 'Failed to load invoices');
        setAllInvoices([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId, showError]);

  const refreshDraftUploadCount = useCallback(async () => {
    if (!tenantId || !selectedBusinessUnitId) {
      setDraftUploadCount(0);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const count = await loadDraftUploadCount(tenantId, token, selectedBusinessUnitId);
      setDraftUploadCount(count);
    } catch (err) {
      console.error('Failed to load draft upload count', err);
    }
  }, [tenantId, selectedBusinessUnitId]);

  useEffect(() => {
    if (tenantId) initializeFormats(tenantId);
    fetchDropdowns();
    fetchInvoices();
    refreshDraftUploadCount();
  }, [tenantId, fetchDropdowns, fetchInvoices, refreshDraftUploadCount]);

  useEffect(() => {
    if (!tenantId || !canUpload) {
      setSageMappingLastRefreshAt(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await apiFetch(
          `conductor/ap-invoice-line-mapping-memory/sage-refresh-status?tenant_id=${tenantId}`
        );
        if (!cancelled) {
          setSageMappingLastRefreshAt(status?.lastRefreshAt || null);
        }
      } catch (err) {
        console.error('Failed to load Sage mapping last refresh date', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, canUpload]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, businessUnitId: selectedBusinessUnitId }));
    setAppliedFilters((prev) => ({ ...prev, businessUnitId: selectedBusinessUnitId }));
  }, [selectedBusinessUnitId]);

  const hasSelectedBusinessUnit = Boolean(selectedBusinessUnitId);

  const tabCounts = useMemo(() => {
    const counts = {
      all: 0,
      draft: draftUploadCount,
      pending_approval: 0,
      approved: 0,
      paid: 0,
      rejected: 0,
      by_vendors: 0,
    };
    if (!hasSelectedBusinessUnit) return counts;

    const buInvoices = userScopedInvoices.filter(
      (inv) => inv.businessUnitId === selectedBusinessUnitId
    );
    counts.all = buInvoices.length;
    const vendorKeys = new Set();
    buInvoices.forEach((inv) => {
      const status = deriveDisplayStatus(inv);
      if (status === 'Pending Approval') counts.pending_approval += 1;
      if (status === 'Approved') counts.approved += 1;
      if (status === 'Paid') counts.paid += 1;
      if (status === 'Rejected') counts.rejected += 1;
      vendorKeys.add(getInvoiceVendorGroupKey(inv));
    });
    counts.by_vendors = vendorKeys.size;
    return counts;
  }, [userScopedInvoices, selectedBusinessUnitId, hasSelectedBusinessUnit, draftUploadCount]);

  useEffect(() => {
    if (!isByVendorsTab) {
      setVendorSearch('');
      setVendorHistoryTarget(null);
    }
  }, [isByVendorsTab]);

  useEffect(() => {
    setVendorHistoryTarget(null);
  }, [selectedBusinessUnitId]);

  const vendorFilteredInvoices = useMemo(() => {
    if (!hasSelectedBusinessUnit || isDraftUploadsTab) return [];

    return userScopedInvoices
      .filter((inv) => inv.businessUnitId === selectedBusinessUnitId)
      .filter((inv) => {
        if (appliedFilters.vendorId && inv.vendorId !== appliedFilters.vendorId) return false;
        if (appliedFilters.invoiceDateFrom && inv.invoiceDate && inv.invoiceDate < appliedFilters.invoiceDateFrom) {
          return false;
        }
        if (appliedFilters.invoiceDateTo && inv.invoiceDate && inv.invoiceDate > appliedFilters.invoiceDateTo) {
          return false;
        }
        if (appliedFilters.dueDateFrom && inv.invoiceDueDate && inv.invoiceDueDate < appliedFilters.dueDateFrom) {
          return false;
        }
        if (appliedFilters.dueDateTo && inv.invoiceDueDate && inv.invoiceDueDate > appliedFilters.dueDateTo) {
          return false;
        }
        return true;
      });
  }, [
    userScopedInvoices,
    appliedFilters,
    hasSelectedBusinessUnit,
    selectedBusinessUnitId,
    isDraftUploadsTab,
  ]);

  const vendorSearchFilteredInvoices = useMemo(() => {
    if (!vendorSearch.trim()) return vendorFilteredInvoices;
    return vendorFilteredInvoices.filter((inv) => invoiceMatchesVendorSearch(inv, vendorSearch, vendorCodeById));
  }, [vendorFilteredInvoices, vendorSearch, vendorCodeById]);

  const vendorSummaryRows = useMemo(() => {
    if (!isByVendorsTab) return [];

    const byVendor = new Map();
    vendorSearchFilteredInvoices.forEach((inv) => {
      const vendorKey = getInvoiceVendorGroupKey(inv);
      const existing = byVendor.get(vendorKey) || {
        vendorId: vendorKey,
        vendorName: inv.vendorName || vendorMap[inv.vendorId] || 'Unknown Vendor',
        invoiceCount: 0,
        totalAmount: 0,
        paidAmount: 0,
        remainingAmount: 0,
      };
      if (!existing.vendorName || existing.vendorName === 'Unknown Vendor') {
        existing.vendorName = inv.vendorName || vendorMap[inv.vendorId] || existing.vendorName;
      }
      existing.invoiceCount += 1;
      existing.totalAmount += invoiceTotalAmount(inv);
      existing.paidAmount += invoicePaidAmount(inv);
      existing.remainingAmount += invoiceRemainingAmount(inv);
      byVendor.set(vendorKey, existing);
    });

    return Array.from(byVendor.values())
      .map((row) => ({ id: row.vendorId, ...row }))
      .sort((a, b) => a.vendorName.localeCompare(b.vendorName));
  }, [isByVendorsTab, vendorSearchFilteredInvoices, vendorMap]);

  const vendorSummaryTotals = useMemo(() => (
    vendorSummaryRows.reduce(
      (acc, row) => ({
        totalAmount: acc.totalAmount + (Number(row.totalAmount) || 0),
        paidAmount: acc.paidAmount + (Number(row.paidAmount) || 0),
        remainingAmount: acc.remainingAmount + (Number(row.remainingAmount) || 0),
        invoiceCount: acc.invoiceCount + (Number(row.invoiceCount) || 0),
      }),
      { totalAmount: 0, paidAmount: 0, remainingAmount: 0, invoiceCount: 0 }
    )
  ), [vendorSummaryRows]);

  const vendorHistoryRows = useMemo(() => {
    if (!vendorHistoryTarget) return [];

    return vendorSearchFilteredInvoices
      .filter((inv) => invoiceMatchesVendorGroup(inv, vendorHistoryTarget))
      .map((inv) => ({
        id: inv.apInvoiceId,
        apInvoiceId: inv.apInvoiceId,
        invoiceNumber: inv.invoiceNumber,
        fileName: getInvoiceFileName(inv) || '—',
        invoiceStatus: deriveDisplayStatus(inv),
        approvalStatus: formatWorkflowStatusLabel(inv.workflowStatus),
        paymentStatus: formatPaymentStatusLabel(inv.paymentStatus),
        invoiceDate: inv.invoiceDate,
        amount: inv.invoiceTotalAmountDue,
        updatedAt: inv.updatedAt,
        raw: inv,
      }))
      .sort((a, b) => String(b.invoiceDate || '').localeCompare(String(a.invoiceDate || '')));
  }, [vendorHistoryTarget, vendorSearchFilteredInvoices]);

  const vendorHistoryTotalAmount = useMemo(
    () => vendorHistoryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [vendorHistoryRows]
  );

  const filteredRows = useMemo(() => {
    if (!hasSelectedBusinessUnit || isDraftUploadsTab || isByVendorsTab) return [];

    const tabKey = TAB_KEYS[activeTab];
    return userScopedInvoices
      .filter((inv) => {
        const displayStatus = deriveDisplayStatus(inv);
        if (tabKey === 'pending_approval' && displayStatus !== 'Pending Approval') {
          return false;
        }
        if (tabKey === 'approved' && displayStatus !== 'Approved') return false;
        if (tabKey === 'paid' && displayStatus !== 'Paid') return false;
        if (tabKey === 'rejected' && displayStatus !== 'Rejected') return false;
        return true;
      })
      .filter((inv) => {
        if (selectedBusinessUnitId && inv.businessUnitId !== selectedBusinessUnitId) return false;
        if (appliedFilters.vendorId && inv.vendorId !== appliedFilters.vendorId) return false;
        if (appliedFilters.status && deriveDisplayStatus(inv).toLowerCase() !== appliedFilters.status.toLowerCase()) {
          return false;
        }
        if (appliedFilters.invoiceDateFrom && inv.invoiceDate && inv.invoiceDate < appliedFilters.invoiceDateFrom) {
          return false;
        }
        if (appliedFilters.invoiceDateTo && inv.invoiceDate && inv.invoiceDate > appliedFilters.invoiceDateTo) {
          return false;
        }
        if (appliedFilters.dueDateFrom && inv.invoiceDueDate && inv.invoiceDueDate < appliedFilters.dueDateFrom) {
          return false;
        }
        if (appliedFilters.dueDateTo && inv.invoiceDueDate && inv.invoiceDueDate > appliedFilters.dueDateTo) {
          return false;
        }
        return true;
      })
      .map((inv) => ({
        id: inv.apInvoiceId,
        apInvoiceId: inv.apInvoiceId,
        invoiceNumber: inv.invoiceNumber,
        vendorName: inv.vendorName || vendorMap[inv.vendorId] || '',
        businessUnitName: businessUnitMap[inv.businessUnitId] || '',
        invoiceDate: inv.invoiceDate,
        invoiceDueDate: inv.invoiceDueDate,
        amount: inv.invoiceTotalAmountDue,
        displayStatus: deriveDisplayStatus(inv),
        raw: inv,
      }));
  }, [userScopedInvoices, activeTab, appliedFilters, vendorMap, businessUnitMap, hasSelectedBusinessUnit, selectedBusinessUnitId, isDraftUploadsTab, isByVendorsTab]);

  const totalAmount = useMemo(
    () => filteredRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [filteredRows]
  );

  const exportInvoiceRows = useMemo(() => {
    if (isDraftUploadsTab) return [];
    if (isByVendorsTab) {
      if (vendorHistoryTarget) {
        return vendorHistoryRows.map((row) => ({
          ...row,
          vendorName: vendorHistoryTarget.vendorName || row.raw?.vendorName || '',
          businessUnitName: businessUnitMap[row.raw?.businessUnitId] || '',
          displayStatus: row.invoiceStatus || deriveDisplayStatus(row.raw),
        }));
      }
      return vendorSearchFilteredInvoices.map((inv) => ({
        id: inv.apInvoiceId,
        apInvoiceId: inv.apInvoiceId,
        invoiceNumber: inv.invoiceNumber,
        vendorName: inv.vendorName || vendorMap[inv.vendorId] || '',
        businessUnitName: businessUnitMap[inv.businessUnitId] || '',
        invoiceDate: inv.invoiceDate,
        invoiceDueDate: inv.invoiceDueDate,
        amount: inv.invoiceTotalAmountDue,
        displayStatus: deriveDisplayStatus(inv),
        raw: inv,
      }));
    }
    return filteredRows;
  }, [
    isDraftUploadsTab,
    isByVendorsTab,
    vendorHistoryTarget,
    vendorHistoryRows,
    vendorSearchFilteredInvoices,
    vendorMap,
    businessUnitMap,
    filteredRows,
  ]);

  const handleExportInvoices = async (format) => {
    setExportAnchor(null);
    if (!canExport) return;
    if (!tenantId || !selectedBusinessUnitId) {
      showError?.('Select a business unit before exporting invoices.');
      return;
    }
    if (!exportInvoiceRows.length) {
      showError?.('No invoices to export for the current view.');
      return;
    }

    try {
      setExporting(true);
      const invoiceIds = new Set(
        exportInvoiceRows.map((row) => row.apInvoiceId || row.raw?.apInvoiceId).filter(Boolean)
      );

      const [allLines, glAccounts, ...splitResults] = await Promise.all([
        apiFetch(
          `conductor/ap-invoice-lines/by-business-unit?tenant_id=${tenantId}&business_unit_id=${selectedBusinessUnitId}`
        ).catch(() => []),
        apiFetch(`conductor/cash-recon/chart-of-account/by-tenant?tenant_id=${tenantId}`).catch(() => []),
        ...[...invoiceIds].map((apInvoiceId) => apiFetch(
          `conductor/ap-invoice-line-splits/by-ap-invoice?tenant_id=${tenantId}&ap_invoice_id=${apInvoiceId}`
        ).catch(() => [])),
      ]);

      const lines = (Array.isArray(allLines) ? allLines : []).filter((line) => invoiceIds.has(line.apInvoiceId));
      const splits = splitResults.flatMap((result) => (Array.isArray(result) ? result : []));

      const exportRows = buildInvoiceExportRows(exportInvoiceRows, {
        lines,
        splits,
        glAccounts: Array.isArray(glAccounts) ? glAccounts : [],
        formatDateOnly,
        dateFormat: config.DateFormat,
      });

      downloadInvoiceExport(exportRows, {
        format,
        fileBaseName: `ap-invoices-${selectedBusinessUnitId}`,
      });
      showSuccess?.(
        format === 'csv'
          ? `Exported ${exportRows.length} row(s) to CSV.`
          : `Exported ${exportRows.length} row(s) to Excel.`
      );
    } catch (err) {
      showError?.(err.message || 'Failed to export invoices.');
    } finally {
      setExporting(false);
    }
  };

  const handlePreview = (row, tab = 0) => {
    setPreviewInvoiceId(row.apInvoiceId);
    setPreviewDraft(null);
    setPreviewInitialTab(tab);
    setPreviewOpen(true);
  };

  const handleExcelParsed = (draft) => {
    setPreviewInvoiceId(null);
    setPreviewDraft(draft);
    setPreviewInitialTab(0);
    setPreviewOpen(true);
    showInfo('Excel parsed. Review extracted data in preview.');
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuInvoice(null);
  };

  const openStageAction = (type, row) => {
    closeRowMenu();
    setStageAction({ type, row });
  };

  const confirmStageAction = async () => {
    if (!canDelete || !stageAction?.row?.apInvoiceId || stageActionSaving) return;
    const { type, row } = stageAction;
    setStageActionSaving(true);
    try {
      if (type === 'delete') {
        await apiFetch(`conductor/ap-invoice-headers/${row.apInvoiceId}`, { method: 'DELETE' });
        showSuccess(`Invoice ${row.invoiceNumber || ''} deleted and removed from Draft. That invoice number can be processed again.`.trim());
      } else {
        const backToDraft = row.displayStatus === 'Pending Approval';
        await apiFetch(`conductor/ap-invoice-headers/${row.apInvoiceId}/revert`, {
          method: 'POST',
          body: JSON.stringify({
            updatedBy: getUserId(),
            userName: getCurrentUserDisplayName(),
            comment: backToDraft
              ? 'Sent back to Draft from Pending Approval'
              : 'Sent back to Pending Approval from Approved',
          }),
        });
        showSuccess(backToDraft
          ? `Invoice ${row.invoiceNumber || ''} sent back to Draft.`
          : `Invoice ${row.invoiceNumber || ''} sent back to Pending Approval.`);
      }
      setStageAction(null);
      fetchInvoices({ silent: true });
      if (type === 'delete') {
        refreshDraftUploadCount();
      }
    } catch (err) {
      showError(err.message || (type === 'delete' ? 'Failed to delete invoice' : 'Failed to revert invoice'));
    } finally {
      setStageActionSaving(false);
    }
  };

  const handleViewContent = (row) => {
    closeRowMenu();
    setContentDialogInvoice(row);
  };

  const columns = useMemo(() => [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice Number',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography component="span" sx={harmonateLinkSx}>
          {params.value}
        </Typography>
      ),
    },
    { field: 'vendorName', headerName: 'Vendor', flex: 1, minWidth: 150 },
    { field: 'businessUnitName', headerName: 'Business Unit', flex: 1, minWidth: 150 },
    {
      field: 'invoiceDate',
      headerName: 'Invoice Date',
      width: 130,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : ''),
    },
    {
      field: 'invoiceDueDate',
      headerName: 'Due Date',
      width: 130,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : ''),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
    },
    {
      field: 'displayStatus',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const chip = statusChipProps(params.value);
        return <Chip label={params.value} size="small" color={chip.color} variant={chip.variant} sx={chip.sx} />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: canDelete ? 96 : 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const action = canDelete ? invoiceStageAction(params.row.displayStatus) : null;
        const revertTitle = params.row.displayStatus === 'Approved'
          ? 'Send back to Pending Approval'
          : 'Send back to Draft';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            {action === 'delete' && (
              <Tooltip title="Delete invoice and free the invoice number">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStageAction({ type: 'delete', row: params.row });
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {action === 'revert' && (
              <Tooltip title={revertTitle}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStageAction({ type: 'revert', row: params.row });
                  }}
                  sx={{ color: ht.text }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setRowMenuAnchor(e.currentTarget);
                setRowMenuInvoice(params.row);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ], [config.DateFormat, config.AmountFormat, canDelete]);

  const handleViewVendorBankDetails = useCallback((vendorId, vendorName) => {
    const invoices = vendorSearchFilteredInvoices.filter((inv) =>
      invoiceMatchesVendorGroup(inv, { vendorId, vendorName })
    );
    setVendorBankDetailsTarget({ vendorId, vendorName, invoices });
  }, [vendorSearchFilteredInvoices]);

  const handleViewVendorHistory = useCallback((vendorId, vendorName) => {
    setVendorHistoryTarget({ vendorId, vendorName });
  }, []);

  const renderStatusChip = (status) => (
    <Chip label={status || '—'} size="small" {...statusChipProps(status)} />
  );

  const vendorHistoryColumns = useMemo(() => [
    {
      field: 'invoiceNumber',
      headerName: 'Invoice Number',
      width: 150,
      renderCell: (params) => (
        <Typography component="span" sx={{ color: ht.text, fontWeight: 500, fontSize: '0.875rem' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    { field: 'fileName', headerName: 'File Name', flex: 1, minWidth: 180 },
    {
      field: 'invoiceStatus',
      headerName: 'Invoice Status',
      width: 150,
      renderCell: (params) => renderStatusChip(params.value),
    },
    {
      field: 'approvalStatus',
      headerName: 'Approval Status',
      width: 150,
      renderCell: (params) => renderStatusChip(params.value),
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      width: 130,
      renderCell: (params) => renderStatusChip(params.value),
    },
    {
      field: 'invoiceDate',
      headerName: 'Invoice Date',
      width: 130,
      valueFormatter: (p) => (p.value ? formatDateOnly(p.value, config.DateFormat) : ''),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      width: 170,
      valueFormatter: (p) => (p.value ? formatDateTime(p.value, config.DateTimeFormat) : ''),
    },
  ], [config.AmountFormat, config.DateFormat, config.DateTimeFormat]);

  const vendorColumns = useMemo(() => [
    { field: 'vendorName', headerName: 'Vendor', flex: 1.5, minWidth: 200 },
    {
      field: 'invoiceCount',
      headerName: 'Invoices',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.stopPropagation();
            handleViewVendorHistory(params.row.vendorId, params.row.vendorName);
          }}
          sx={{
            fontWeight: 600,
            textAlign: 'right',
            width: '100%',
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: 'totalAmount',
      headerName: 'Total Amount',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
    },
    {
      field: 'paidAmount',
      headerName: 'Paid',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
    },
    {
      field: 'remainingAmount',
      headerName: 'Remaining',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
      renderCell: (params) => (
        <Typography
          component="span"
          sx={{
            fontWeight: Number(params.value) > 0 ? 600 : 400,
            color: Number(params.value) > 0 ? ht.red : 'inherit',
            fontSize: '0.875rem',
          }}
        >
          {params.value != null ? formatAmount(params.value, config.AmountFormat) : ''}
        </Typography>
      ),
    },
    {
      field: 'viewBankDetails',
      headerName: '',
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View invoice bank details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewVendorBankDetails(params.row.vendorId, params.row.vendorName);
            }}
            sx={{ color: ht.text }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [config.AmountFormat, handleViewVendorBankDetails, handleViewVendorHistory]);

  const handleApply = () => setAppliedFilters({ ...filters });
  const handleReset = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const handleUploadComplete = () => {
    fetchInvoices({ silent: true });
    setDraftUploadRefreshKey((key) => key + 1);
    refreshDraftUploadCount();
    setActiveTab(TAB_KEYS.indexOf('draft'));
  };

  const handleDraftUploadCountChange = useCallback((count) => {
    setDraftUploadCount(count);
  }, []);

  const handleRefresh = () => {
    if (isDraftUploadsTab) {
      setDraftUploadRefreshKey((key) => key + 1);
    } else {
      fetchInvoices();
    }
    refreshDraftUploadCount();
  };

  const handleRefreshFromSage = async () => {
    if (!tenantId || seeding) return;
    try {
      setSeeding(true);
      const result = await apiFetch('conductor/ap-invoice-line-mapping-memory/refresh-from-sage', {
        method: 'POST',
        body: JSON.stringify({
          tenantId,
          overwriteExisting: overwriteExistingMappings,
        }),
      });
      setSeedOpen(false);
      setOverwriteExistingMappings(false);
      if (result?.lastRefreshAt) {
        setSageMappingLastRefreshAt(result.lastRefreshAt);
      }
      const inserted = Number(result?.inserted) || 0;
      const updated = Number(result?.updated) || 0;
      const skippedExisting = Number(result?.skippedExisting) || 0;
      const skippedVendor = Number(result?.skippedUnmappedVendor) || 0;
      const skippedGl = Number(result?.skippedUnmappedGl) || 0;
      const skippedDesc = Number(result?.skippedUnusableDescription) || 0;
      showSuccess(
        result?.message
        || `Sage mappings refreshed. Inserted ${inserted}, updated ${updated}, skipped existing ${skippedExisting}, unmapped vendor ${skippedVendor}, unmapped GL ${skippedGl}, empty description ${skippedDesc}.`
      );
    } catch (err) {
      showError(err.message || 'Failed to refresh mappings from Sage');
    } finally {
      setSeeding(false);
    }
  };

  const handleTabChange = (_, tabIndex) => {
    setActiveTab(tabIndex);
    if (TAB_KEYS[tabIndex] === 'draft') {
      setDraftUploadRefreshKey((key) => key + 1);
    } else {
      fetchInvoices({ silent: true });
    }
    refreshDraftUploadCount();
  };

  if (loading && allInvoices.length === 0 && !isDraftUploadsTab) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
      </Box>
    );
  }

  return (
    <Box sx={harmonatePageSx}>
      <ApHubBreadcrumb variant="harmonate" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography sx={harmonatePageTitleSx}>Invoices</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canUpload && (
            <Tooltip
              title="Load Sage paid AP coding into learned GL mappings (vendor + line description)"
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={seeding ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                  onClick={() => setSeedOpen(true)}
                  disabled={seeding || !tenantId}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh from Sage
                </Button>
              </span>
            </Tooltip>
          )}
          {canUpload && (
            <Tooltip
              title={hasSelectedBusinessUnit ? '' : 'Select a business unit on the Dashboard to upload an invoice'}
              disableHoverListener={hasSelectedBusinessUnit}
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadOpen(true)}
                  disabled={!hasSelectedBusinessUnit}
                  sx={harmonateProceedButtonSx}
                >
                  Upload Invoice
                </Button>
              </span>
            </Tooltip>
          )}
          {canAdd && (
            <>
              <Button
                variant="contained"
                endIcon={<KeyboardArrowDownIcon />}
                startIcon={<AddIcon />}
                onClick={(e) => setCreateAnchor(e.currentTarget)}
                sx={harmonateBlueButtonSx}
              >
                Create Invoice
              </Button>
              <Menu anchorEl={createAnchor} open={Boolean(createAnchor)} onClose={() => setCreateAnchor(null)}>
                <MenuItem onClick={() => { setCreateAnchor(null); setEditInvoiceId(null); setManualOpen(true); }}>
                  <EditIcon fontSize="small" sx={{ mr: 1 }} /> Manual Entry
                </MenuItem>
                <MenuItem onClick={() => { setCreateAnchor(null); showInfo('API Import integration coming soon.'); }}>
                  API Import
                </MenuItem>
              </Menu>
            </>
          )}
          <IconButton onClick={handleRefresh} disabled={loading} sx={harmonatePlainIconButtonSx}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...harmonateCardSx, mb: 2, flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={harmonateTabsSx}
        >
          <Tab label={<TabLabel text="All Invoices" count={tabCounts.all} active={activeTab === 0} />} />
          <Tab label={<TabLabel text="Draft" count={tabCounts.draft} tone="warning" active={activeTab === 1} />} />
          <Tab label={<TabLabel text="Pending Approval" count={tabCounts.pending_approval} active={activeTab === 2} />} />
          <Tab label={<TabLabel text="Approved" count={tabCounts.approved} tone="success" active={activeTab === 3} />} />
          <Tab label={<TabLabel text="Paid" count={tabCounts.paid} tone="info" active={activeTab === 4} />} />
          <Tab label={<TabLabel text="Rejected" count={tabCounts.rejected} tone="warning" active={activeTab === TAB_KEYS.indexOf('rejected')} />} />
          <Tab label={<TabLabel text="By Vendors" count={tabCounts.by_vendors} active={activeTab === TAB_KEYS.indexOf('by_vendors')} />} />
        </Tabs>

        {!isDraftUploadsTab ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderTop: `1px solid ${ht.line}`,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={exporting ? <CircularProgress size={16} /> : <GetAppIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => {
              if (!canExport) return;
              setExportAnchor(e.currentTarget);
            }}
            disabled={exporting || !hasSelectedBusinessUnit || exportInvoiceRows.length === 0 || !canExport}
            sx={{ textTransform: 'none', borderColor: '#8CD867', color: '#4a6741' }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => handleExportInvoices('xlsx')} disabled={exporting}>
              Excel (.xlsx)
            </MenuItem>
            <MenuItem onClick={() => handleExportInvoices('csv')} disabled={exporting}>
              CSV (.csv)
            </MenuItem>
          </Menu>
          <Button
            variant={showFilters ? 'contained' : 'outlined'}
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((prev) => !prev)}
            sx={showFilters ? harmonateProceedButtonSx : harmonateUploadOutlinedSx}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>
        ) : null}

        {!isDraftUploadsTab && showFilters ? (
          <Box sx={{ p: 2.5, borderTop: `1px solid ${ht.line}`, bgcolor: ht.white }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1.5,
                flexWrap: { xs: 'wrap', xl: 'nowrap' },
              }}
            >
              <TextField
                select
                size="small"
                label="Vendor"
                value={filters.vendorId}
                onChange={(e) => setFilters((f) => ({ ...f, vendorId: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ flex: 1, minWidth: 140 }}
              >
                <MenuItem value="">All Vendors</MenuItem>
                {vendors.map((v) => (
                  <MenuItem key={v.vendorId} value={v.vendorId}>{v.vendorName}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="date"
                label="Invoice Date From"
                InputLabelProps={{ shrink: true }}
                value={filters.invoiceDateFrom}
                onChange={(e) => setFilters((f) => ({ ...f, invoiceDateFrom: e.target.value }))}
                sx={{ flex: 1, minWidth: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="Invoice Date To"
                InputLabelProps={{ shrink: true }}
                value={filters.invoiceDateTo}
                onChange={(e) => setFilters((f) => ({ ...f, invoiceDateTo: e.target.value }))}
                sx={{ flex: 1, minWidth: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="Due Date From"
                InputLabelProps={{ shrink: true }}
                value={filters.dueDateFrom}
                onChange={(e) => setFilters((f) => ({ ...f, dueDateFrom: e.target.value }))}
                sx={{ flex: 1, minWidth: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="Due Date To"
                InputLabelProps={{ shrink: true }}
                value={filters.dueDateTo}
                onChange={(e) => setFilters((f) => ({ ...f, dueDateTo: e.target.value }))}
                sx={{ flex: 1, minWidth: 150 }}
              />
              {!isByVendorsTab ? (
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                  sx={{ flex: 1, minWidth: 130 }}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Pending Approval">Pending Approval</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </TextField>
              ) : null}
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, pb: 0.25 }}>
                <Button
                  variant="outlined"
                  startIcon={<ResetIcon />}
                  onClick={handleReset}
                  sx={harmonateCancelButtonSx}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApply}
                  disabled={!hasSelectedBusinessUnit}
                  sx={harmonateProceedButtonSx}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        ) : null}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {isDraftUploadsTab ? (
          <PendingInvoicesTab
            refreshKey={draftUploadRefreshKey}
            onCountChange={handleDraftUploadCountChange}
            onFileDeleted={() => fetchInvoices({ silent: true })}
          />
        ) : isByVendorsTab ? (
          <>
            <Box
              sx={{
                ...harmonateSectionHeaderSx,
                flexShrink: 0,
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              {vendorHistoryTarget ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setVendorHistoryTarget(null)}
                    sx={{ textTransform: 'none', color: ht.text }}
                  >
                    Back to Vendors
                  </Button>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                    {vendorHistoryTarget.vendorName} — Invoice History ({vendorHistoryRows.length})
                  </Typography>
                </Box>
              ) : (
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
                  Vendors ({vendorSummaryRows.length})
                </Typography>
              )}
              <TextField
                size="small"
                placeholder="Search vendor name, vendor ID, or invoice/file name"
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                sx={{ width: { xs: '100%', sm: 340 }, bgcolor: ht.white }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              {vendorHistoryTarget ? (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                  Total Amount: {formatAmount(vendorHistoryTotalAmount, config.AmountFormat)}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
                    Total: {formatAmount(vendorSummaryTotals.totalAmount, config.AmountFormat)}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.green }}>
                    Paid: {formatAmount(vendorSummaryTotals.paidAmount, config.AmountFormat)}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.red }}>
                    Remaining: {formatAmount(vendorSummaryTotals.remainingAmount, config.AmountFormat)}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              {!hasSelectedBusinessUnit ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">Select a business unit to view vendor totals.</Typography>
                </Box>
              ) : vendorHistoryTarget ? (
                <DataGridPro
                  rows={vendorHistoryRows}
                  columns={vendorHistoryColumns}
                  loading={loading}
                  rowHeight={44}
                  disableRowSelectionOnClick
                  autoHeight={false}
                  onRowClick={(params) => handlePreview(params.row)}
                  pagination
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  sx={{
                    ...harmonateDataGridSx,
                    height: '100%',
                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                  }}
                />
              ) : (
                <DataGridPro
                  rows={vendorSummaryRows}
                  columns={vendorColumns}
                  loading={loading}
                  rowHeight={44}
                  disableRowSelectionOnClick
                  autoHeight={false}
                  pagination
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  sx={{ ...harmonateDataGridSx, height: '100%' }}
                />
              )}
            </Box>
            <Box sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${ht.line}`, bgcolor: ht.white, flexShrink: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {!hasSelectedBusinessUnit
                  ? 'No business unit selected'
                  : vendorHistoryTarget
                    ? `Showing ${vendorHistoryRows.length} invoice${vendorHistoryRows.length === 1 ? '' : 's'} for ${vendorHistoryTarget.vendorName}`
                    : vendorSearch.trim()
                      ? `Showing ${vendorSummaryRows.length} vendor${vendorSummaryRows.length === 1 ? '' : 's'} matching "${vendorSearch.trim()}" across ${vendorSummaryTotals.invoiceCount} invoice${vendorSummaryTotals.invoiceCount === 1 ? '' : 's'}`
                      : `Showing ${vendorSummaryRows.length} vendor${vendorSummaryRows.length === 1 ? '' : 's'} across ${vendorSummaryTotals.invoiceCount} invoice${vendorSummaryTotals.invoiceCount === 1 ? '' : 's'}`}
              </Typography>
            </Box>
          </>
        ) : (
          <>
        <Box sx={{ ...harmonateSectionHeaderSx, flexShrink: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
            Invoices ({filteredRows.length})
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
            Total Amount: {formatAmount(totalAmount, config.AmountFormat)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {!hasSelectedBusinessUnit ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">Select a business unit to view invoices.</Typography>
            </Box>
          ) : (
            <DataGridPro
              rows={filteredRows}
              columns={columns}
              loading={loading}
              rowHeight={44}
              disableRowSelectionOnClick
              autoHeight={false}
              onRowClick={(params) => handlePreview(params.row)}
              pagination
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{
                ...harmonateDataGridSx,
                height: '100%',
                '& .MuiDataGrid-row': { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
        <Box sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${ht.line}`, bgcolor: ht.white, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {hasSelectedBusinessUnit
              ? `Showing ${filteredRows.length} invoice${filteredRows.length === 1 ? '' : 's'}`
              : 'No business unit selected'}
          </Typography>
        </Box>
          </>
        )}
      </Paper>

      <InvoiceUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        tenantId={tenantId}
        businessUnitId={selectedBusinessUnitId}
        businessUnitName={selectedBusinessUnitName}
        onExcelParsed={handleExcelParsed}
        onUploadComplete={handleUploadComplete}
        showInfo={showInfo}
        showSuccess={showSuccess}
        showError={showError}
      />

      <InvoiceManualEntryDialog
        open={manualOpen}
        onClose={() => {
          setManualOpen(false);
          setEditInvoiceId(null);
        }}
        tenantId={tenantId}
        vendors={vendors}
        businessUnits={businessUnits}
        businessUnitId={selectedBusinessUnitId}
        businessUnitName={selectedBusinessUnitName}
        invoiceId={editInvoiceId}
        onSaved={() => {
          fetchInvoices();
        }}
        showSuccess={showSuccess}
        showError={showError}
      />

      <InvoicePreviewDrawer
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewInvoiceId(null);
          setPreviewDraft(null);
        }}
        invoiceId={previewInvoiceId}
        draftData={previewDraft}
        tenantId={tenantId}
        initialTab={previewInitialTab}
        onConfirmed={fetchInvoices}
        showSuccess={showSuccess}
        showError={showError}
      />

      <Menu anchorEl={rowMenuAnchor} open={Boolean(rowMenuAnchor)} onClose={closeRowMenu}>
        <MenuItem disabled>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleViewContent(rowMenuInvoice)}>
          <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Content</ListItemText>
        </MenuItem>
        {canDelete && invoiceStageAction(rowMenuInvoice?.displayStatus) === 'delete' && (
          <MenuItem onClick={() => openStageAction('delete', rowMenuInvoice)}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
        {canDelete && invoiceStageAction(rowMenuInvoice?.displayStatus) === 'revert' && (
          <MenuItem onClick={() => openStageAction('revert', rowMenuInvoice)}>
            <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>
              {rowMenuInvoice?.displayStatus === 'Approved' ? 'Revert to Pending Approval' : 'Revert to Draft'}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={Boolean(stageAction)}
        onClose={() => !stageActionSaving && setStageAction(null)}
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {stageAction?.type === 'delete'
            ? 'Delete invoice?'
            : (stageAction?.row?.displayStatus === 'Approved'
              ? 'Revert to Pending Approval?'
              : 'Revert to Draft?')}
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            {stageAction?.type === 'delete'
              ? `Delete invoice ${stageAction?.row?.invoiceNumber || ''}? This removes it from the system and frees the invoice number so it can be processed again.`
              : (stageAction?.row?.displayStatus === 'Approved'
                ? `Send invoice ${stageAction?.row?.invoiceNumber || ''} back to Pending Approval? It stays in the system.`
                : `Send invoice ${stageAction?.row?.invoiceNumber || ''} back to Draft? It stays in the system and can be corrected or submitted again.`)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setStageAction(null)}
            variant="outlined"
            disabled={stageActionSaving}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmStageAction}
            variant="contained"
            disabled={stageActionSaving}
            sx={stageAction?.type === 'delete' ? harmonateAbortButtonSx : harmonateProceedButtonSx}
          >
            {stageActionSaving
              ? 'Saving...'
              : (stageAction?.type === 'delete' ? 'Delete' : 'Revert')}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceContentDialog
        open={Boolean(contentDialogInvoice)}
        onClose={() => setContentDialogInvoice(null)}
        invoiceId={contentDialogInvoice?.apInvoiceId}
        invoiceNumber={contentDialogInvoice?.invoiceNumber}
      />

      <VendorInvoiceBankDetailsDialog
        open={Boolean(vendorBankDetailsTarget)}
        onClose={() => setVendorBankDetailsTarget(null)}
        tenantId={tenantId}
        vendorId={vendorBankDetailsTarget?.vendorId}
        vendorName={vendorBankDetailsTarget?.vendorName}
        invoices={vendorBankDetailsTarget?.invoices || []}
      />

      <Dialog
        open={seedOpen}
        onClose={() => !seeding && setSeedOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Mappings from Sage</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This refreshes learned mappings from Sage paid AP coding
            (vendor + line description → GL account / property). New invoices can then auto-fill GL in Invoice Review.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            {sageMappingLastRefreshAt
              ? 'This run loads only the delta since the last refresh date. Existing learned mappings are left unchanged unless you choose overwrite.'
              : 'There is no last refresh date yet, so this run loads the full Sage paid history. Existing learned mappings are left unchanged unless you choose overwrite.'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
            Last refresh date
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            {lastRefreshLabel(sageMappingLastRefreshAt, config.DateTimeFormat)}
          </Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={overwriteExistingMappings}
                onChange={(e) => setOverwriteExistingMappings(e.target.checked)}
                disabled={seeding}
              />
            )}
            label="Overwrite existing learned mappings"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setSeedOpen(false)}
            disabled={seeding}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRefreshFromSage}
            disabled={seeding}
            startIcon={seeding ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
            sx={harmonateProceedButtonSx}
          >
            {seeding ? 'Refreshing…' : 'Refresh from Sage'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default Invoices;
